import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Calendar, Plus, Clock, Users, DollarSign, Image, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import ConfirmModal from '../components/ConfirmModal';
import axios from 'axios';
import { toast } from 'sonner';
import { trackModuleVisit, trackEvent } from '@/lib/posthog';

const BookingsPage = () => {
  const { user, selectedBuilding } = useAuth();
  const [areas, setAreas] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createAreaOpen, setCreateAreaOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [buildings, setBuildings] = useState([]);
  
  const [areaForm, setAreaForm] = useState({
    building_id: '',
    name: '',
    description: '',
    capacity: '',
    image_url: ''
  });

  const [bookingForm, setBookingForm] = useState({
    date: '',
    start_time: '',
    end_time: ''
  });

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Track module visit on mount
  useEffect(() => {
    trackModuleVisit('reservas');
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuilding && selectedBuilding !== 'all') {
        fetchAreas();
        fetchBookings();
    } else {
        // If all selected, fetch for all? Or force selection?
        // Let's fetch all for admin
        fetchAreas();
        fetchBookings();
    }
  }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const res = await axios.get(`${API}/buildings`);
      setBuildings(res.data);
      if(res.data.length > 0 && (!selectedBuilding || selectedBuilding === 'all')) {
         setAreaForm(prev => ({...prev, building_id: res.data[0].id}));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}` : '';
      const res = await axios.get(`${API}/common-areas${query}`);
      setAreas(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}&include_cancelled=true` : '?include_cancelled=true';
      const res = await axios.get(`${API}/bookings${query}`);
      // Filter client side for my bookings if resident
      setMyBookings(res.data.filter(b => b.user_id === user.id));
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancelClick = (booking) => {
    setBookingToCancel(booking);
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!bookingToCancel) return;
    
    setCancelLoading(true);
    try {
      await axios.patch(`${API}/bookings/${bookingToCancel.id}/cancel`);
      // Optimistic UI update
      setMyBookings(prev => 
        prev.map(b => b.id === bookingToCancel.id ? { ...b, status: 'cancelled' } : b)
      );
      toast.success('Reserva cancelada exitosamente');
      trackEvent('reserva_cancelled', { booking_id: bookingToCancel.id, area: bookingToCancel.area_name });
      setCancelModalOpen(false);
      setBookingToCancel(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar reserva');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCreateArea = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/common-areas`, {
        ...areaForm,
        capacity: parseInt(areaForm.capacity)
      });
      toast.success('Zona común creada');
      setCreateAreaOpen(false);
      fetchAreas();
    } catch (error) {
      toast.error('Error al crear zona');
    }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    try {
      // Combine date and time
      const start = `${bookingForm.date}T${bookingForm.start_time}:00`;
      const end = `${bookingForm.date}T${bookingForm.end_time}:00`;
      
      await axios.post(`${API}/bookings`, {
        building_id: selectedArea.building_id,
        area_id: selectedArea.id,
        start_time: start,
        end_time: end
      });
      
      toast.success('Reserva confirmada');
      // Track reservation creation
      trackEvent('reserva_created', { building_id: selectedArea.building_id, area: selectedArea.name });
      setBookOpen(false);
      fetchBookings();
    } catch (error) {
        if (error.response?.data?.detail) {
            toast.error(error.response.data.detail);
        } else {
            toast.error('Error al reservar');
        }
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary">Reservas</h1>
            <p className="text-text-secondary mt-1">Espacios comunes y amenidades</p>
          </div>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <Dialog open={createAreaOpen} onOpenChange={setCreateAreaOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent/90 text-white rounded-full">
                  <Plus className="h-5 w-5 mr-2" />
                  Nueva Zona
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear Zona Común</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateArea} className="space-y-4">
                  <div>
                    <Label>Conjunto</Label>
                    <Select value={areaForm.building_id} onValueChange={(val) => setAreaForm({...areaForm, building_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona conjunto" />
                      </SelectTrigger>
                      <SelectContent>
                        {buildings.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nombre</Label>
                    <Input value={areaForm.name} onChange={e => setAreaForm({...areaForm, name: e.target.value})} required placeholder="Ej: Salón Comunal" />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Textarea value={areaForm.description} onChange={e => setAreaForm({...areaForm, description: e.target.value})} placeholder="Detalles de la zona..." />
                  </div>
                  <div>
                    <Label>Capacidad (personas)</Label>
                    <Input type="number" value={areaForm.capacity} onChange={e => setAreaForm({...areaForm, capacity: e.target.value})} required />
                  </div>
                  <div>
                    <Label>URL Imagen (Opcional)</Label>
                    <Input value={areaForm.image_url} onChange={e => setAreaForm({...areaForm, image_url: e.target.value})} placeholder="https://..." />
                  </div>
                  <Button type="submit" className="w-full bg-primary">Crear</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
           <div className="flex items-center justify-center h-64">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
           </div>
        ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {areas.length === 0 ? (
                    <div className="col-span-full text-center py-12 bg-white rounded-xl border border-slate-100">
                        <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No hay zonas comunes registradas</p>
                    </div>
                ) : (
                    areas.map(area => (
                        <Card key={area.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                            <div className="h-48 bg-slate-100 relative">
                                {area.image_url ? (
                                    <img src={area.image_url} alt={area.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-slate-50">
                                        <Image className="h-12 w-12 text-slate-300" />
                                    </div>
                                )}
                                <div className="absolute top-4 right-4 bg-white/90 px-3 py-1 rounded-full text-xs font-semibold text-primary shadow-sm">
                                    Capacidad: {area.capacity}
                                </div>
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-primary mb-2">{area.name}</h3>
                                <p className="text-text-secondary text-sm mb-6 line-clamp-2">{area.description}</p>
                                <Button 
                                    className="w-full bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white"
                                    onClick={() => {
                                        setSelectedArea(area);
                                        setBookOpen(true);
                                    }}
                                >
                                    Reservar
                                </Button>
                            </div>
                        </Card>
                    ))
                )}
            </div>
        )}

        {/* Bookings List */}
        {myBookings.length > 0 && (
            <div className="mt-12">
                <h2 className="text-xl font-heading font-bold text-primary mb-6">Mis Reservas</h2>
                <div className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
                    {myBookings.map(booking => {
                        const isCancelled = booking.status === 'cancelled';
                        const canCancel = !isCancelled && (
                          booking.user_id === user.id || 
                          user?.role === 'admin' || 
                          user?.role === 'super_admin'
                        );
                        
                        return (
                          <div 
                            key={booking.id} 
                            className={`p-4 flex items-center justify-between ${isCancelled ? 'opacity-60 bg-slate-50' : ''}`}
                            data-testid={`booking-item-${booking.id}`}
                          >
                              <div>
                                  <p className={`font-semibold ${isCancelled ? 'text-slate-500 line-through' : 'text-primary'}`}>
                                    {booking.area_name}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                      {new Date(booking.start_time).toLocaleDateString()} • {new Date(booking.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(booking.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {isCancelled ? (
                                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Cancelada</Badge>
                                ) : (
                                  <>
                                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Confirmada</Badge>
                                    {canCancel && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleCancelClick(booking)}
                                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                                        data-testid={`cancel-booking-${booking.id}`}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                          </div>
                        );
                    })}
                </div>
            </div>
        )}

        {/* Booking Dialog */}
        <Dialog open={bookOpen} onOpenChange={setBookOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Reservar {selectedArea?.name}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleBook} className="space-y-4">
                    <div>
                        <Label>Fecha</Label>
                        <Input type="date" value={bookingForm.date} onChange={e => setBookingForm({...bookingForm, date: e.target.value})} required min={new Date().toISOString().split('T')[0]} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Hora Inicio</Label>
                            <Input type="time" value={bookingForm.start_time} onChange={e => setBookingForm({...bookingForm, start_time: e.target.value})} required />
                        </div>
                        <div>
                            <Label>Hora Fin</Label>
                            <Input type="time" value={bookingForm.end_time} onChange={e => setBookingForm({...bookingForm, end_time: e.target.value})} required />
                        </div>
                    </div>
                    <Button type="submit" className="w-full bg-primary mt-2">Confirmar Reserva</Button>
                </form>
            </DialogContent>
        </Dialog>

        {/* Cancel Confirmation Modal */}
        <ConfirmModal
          open={cancelModalOpen}
          onOpenChange={setCancelModalOpen}
          title="Cancelar Reserva"
          description={`¿Estás seguro de cancelar tu reserva de "${bookingToCancel?.area_name}"? El espacio quedará disponible para otros residentes.`}
          confirmText="Sí, Cancelar"
          onConfirm={handleCancelConfirm}
          loading={cancelLoading}
          variant="warning"
        />
      </div>
    </DashboardLayout>
  );
};

export default BookingsPage;
